<template>
  <v-dialog v-model="dialog" persistent max-width="500px">
    <v-card>
      <v-card-title class="redColor text-white">
        <span v-if="edit" class="headline">{{
          $t("oem_stock_watchlist.form.edit_title")
        }}</span>
        <span v-else class="headline">{{
          $t("oem_stock_watchlist.form.add_title")
        }}</span>
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="errorMessage"
          :model-value="true"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-container grid-list-md>
            <v-row wrap>
              <v-col cols="12" xs="12">
                <v-autocomplete
                  v-if="!edit"
                  v-model="form.oemCode"
                  v-model:search="oemSearch"
                  :items="suggestions"
                  :loading="loadingSuggestions"
                  :rules="rules.required"
                  :label="$t('oem_stock_watchlist.form.oem')"
                  item-title="oemCode"
                  item-value="oemCode"
                  :filter="() => true"
                  color="red-darken-1"
                  variant="solo"
                  autocomplete="off"
                  :no-data-text="$t('oem_stock_watchlist.form.no_suggestions')"
                  @update:search="fetchSuggestions"
                >
                  <template #item="{ props, item }">
                    <v-list-item
                      v-bind="props"
                      :disabled="item.raw.onWatchlist"
                      :subtitle="suggestionSubtitle(item.raw)"
                    />
                  </template>
                </v-autocomplete>
                <v-text-field
                  v-else
                  v-model="form.oemCode"
                  :rules="rules.required"
                  :label="$t('oem_stock_watchlist.form.oem')"
                  disabled
                  color="red-darken-1"
                  variant="solo"
                />
              </v-col>
              <v-col cols="6" xs="12">
                <v-text-field
                  v-model.number="form.minimumQuantity"
                  :rules="rules.minimumQuantity"
                  :label="$t('oem_stock_watchlist.form.minimum')"
                  color="red-darken-1"
                  type="number"
                  min="1"
                  step="1"
                  variant="solo"
                />
              </v-col>
              <v-col v-if="edit" cols="6" xs="12">
                <v-switch
                  v-model="form.enabled"
                  :label="$t('oem_stock_watchlist.form.enabled')"
                  color="red-darken-1"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="red-darken-1" variant="outlined" @click="close">
          {{ $t("oem_stock_watchlist.form.close") }}
        </v-btn>
        <v-btn
          :disabled="!valid"
          :loading="loading"
          color="green-darken-3"
          variant="outlined"
          @click="save"
        >
          {{ $t("oem_stock_watchlist.form.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import postRequest from "../../api/postRequest";

const ADMIN_ROLES = ["LekoAdmin", "LekoEmployee", "SuperAdmin"];
const INSTITUTION_SELECTOR_ROLES = ["LekoAdmin", "SuperAdmin"];

export default {
  props: {
    institutionId: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    dialog: false,
    loading: false,
    edit: false,
    valid: false,
    errorMessage: "",
    oemSearch: "",
    suggestions: [],
    loadingSuggestions: false,
    suggestTimer: null,
    entryId: null,
    form: {
      oemCode: "",
      minimumQuantity: 1,
      enabled: true,
    },
    rules: {
      required: [(v) => !!v || "Please fill this field"],
      minimumQuantity: [
        (v) => !!v || "Please fill this field",
        (v) => Number.isInteger(Number(v)) && Number(v) >= 1 || "Minimum must be an integer >= 1",
      ],
    },
  }),
  methods: {
    isGlobalAdmin() {
      return ADMIN_ROLES.includes(this.$store.getters.role);
    },
    usesSelectedInstitution() {
      return INSTITUTION_SELECTOR_ROLES.includes(this.$store.getters.role);
    },
    buildQuery(extra = "") {
      let query = extra;
      if (this.usesSelectedInstitution()) {
        if (!this.institutionId) return "";
        const inst = `institution_id=${this.institutionId}`;
        query = query ? `${query}&${inst}` : inst;
      } else if (this.isGlobalAdmin()) {
        const inst = `institution_id=${this.$store.getters.institutionId}`;
        query = query ? `${query}&${inst}` : inst;
      }
      return query;
    },
    open(edit, data) {
      this.edit = edit;
      this.errorMessage = "";
      this.oemSearch = "";
      this.suggestions = [];
      if (edit && data) {
        this.entryId = data._id;
        this.form = {
          oemCode: data.oemCode,
          minimumQuantity: data.minimumQuantity,
          enabled: data.enabled,
        };
      } else {
        this.entryId = null;
        this.form = {
          oemCode: "",
          minimumQuantity: 1,
          enabled: true,
        };
      }
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.loading = false;
      this.edit = false;
      this.errorMessage = "";
      this.oemSearch = "";
      this.suggestions = [];
      this.entryId = null;
      this.form = {
        oemCode: "",
        minimumQuantity: 1,
        enabled: true,
      };
      this.$emit("close");
    },
    localizedLabel(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      const lang = this.$store.getters.language;
      return value[lang] || value.it || value.en || "";
    },
    suggestionSubtitle(item) {
      const parts = [
        this.localizedLabel(item.title),
        this.localizedLabel(item.category),
        `${item.activeCount} active`,
      ].filter(Boolean);
      if (item.onWatchlist) {
        parts.push(this.$t("oem_stock_watchlist.form.on_watchlist"));
      }
      return parts.join(" · ");
    },
    fetchSuggestions(query) {
      clearTimeout(this.suggestTimer);
      const q = (query || "").trim();
      if (q.length < 2) {
        this.suggestions = [];
        return;
      }
      this.suggestTimer = setTimeout(() => {
        this.loadingSuggestions = true;
        const params = `q=${encodeURIComponent(q)}&limit=25`;
        getRequest("/oem-stock-watchlist/suggest", this.buildQuery(params))
          .then((response) => {
            this.suggestions = response.suggestions || [];
          })
          .catch(() => {
            this.suggestions = [];
          })
          .finally(() => {
            this.loadingSuggestions = false;
          });
      }, 300);
    },
    async save() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      this.errorMessage = "";

      try {
        if (this.edit) {
          await putRequest(
            `/oem-stock-watchlist/${this.entryId}`,
            this.buildQuery(),
            {
              minimumQuantity: Number(this.form.minimumQuantity),
              enabled: this.form.enabled,
            },
          );
        } else {
          const body = {
            oemCode: this.form.oemCode.trim(),
            minimumQuantity: Number(this.form.minimumQuantity),
          };
          if (this.isGlobalAdmin() && !this.usesSelectedInstitution()) {
            body.institutionId = this.$store.getters.institutionId;
          }
          await postRequest.object("/oem-stock-watchlist", body);
        }
        this.close();
      } catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message;
        if (status === 409) {
          this.errorMessage = this.$t("oem_stock_watchlist.duplicate_error");
        } else {
          this.errorMessage = message || "Something went wrong";
        }
        this.loading = false;
      }
    },
  },
};
</script>
